/**
 * Clients Service Layer
 *
 * Provides a clean, type-safe interface for client operations.
 * Used by React Query hooks for data fetching and mutations.
 */

import { apiClient } from './api.client';
import type { ClientsResponse, ClientFilters } from '@lnr-v2/types';

/**
 * Build query params from filters
 */
function buildSearchParams(filters: ClientFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  if (filters.search) params.append('search', filters.search);
  if (filters.isActive !== undefined)
    params.append('isActive', filters.isActive.toString());
  if (filters.isFeatured !== undefined)
    params.append('isFeatured', filters.isFeatured.toString());
  if (filters.industry) params.append('industry', filters.industry);
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

  return params;
}

/**
 * Clients Service
 * All client-related API operations
 */
export const clientsService = {
  /**
   * Get all clients with pagination and filters
   */
  async getAll(filters: ClientFilters): Promise<ClientsResponse> {
    const params = buildSearchParams(filters);
    return apiClient
      .get(`clients?${params.toString()}`)
      .json<ClientsResponse>();
  },

  /**
   * Get a single client by ID
   */
  async getById(id: string): Promise<ClientsResponse['clients'][0]> {
    return apiClient.get(`clients/${id}`).json<ClientsResponse['clients'][0]>();
  },

  /**
   * Create a new client
   */
  async create(
    data: Partial<ClientsResponse['clients'][0]>
  ): Promise<ClientsResponse['clients'][0]> {
    return apiClient
      .post('clients', { json: data })
      .json<ClientsResponse['clients'][0]>();
  },

  /**
   * Update an existing client
   */
  async update(
    id: string,
    data: Partial<ClientsResponse['clients'][0]>
  ): Promise<ClientsResponse['clients'][0]> {
    return apiClient
      .put(`clients/${id}`, { json: data })
      .json<ClientsResponse['clients'][0]>();
  },

  /**
   * Delete a client
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`clients/${id}`);
  },

  /**
   * Reorder clients by updating their sortOrder
   */
  async reorder(
    updates: Array<{ id: string; sortOrder: number }>
  ): Promise<void> {
    await apiClient.put('clients/reorder', { json: { updates } });
  },
};
